<template>
  <div class="nx-key-value-editor-wrapper">
    <nx-relation-editor-toolbar :title="title">
      <template slot="actions">
        <v-tooltip top v-if="hint">
          <v-icon slot="activator">info_outline</v-icon>
          <span v-html="hint"></span>
        </v-tooltip>
        <v-btn icon small @click="addCardAt(0)" v-if="!disableAdd">
          <v-icon>add</v-icon>
        </v-btn>
      </template>
    </nx-relation-editor-toolbar>

    <transition-group
      name="relation-list"
      tag="div"
      class="nx-key-value--list"
      :class="{ moving: moving }"
      ref="list">

      <v-layout
        v-for="(item, index) in value"
        :key="item.uuid"
        class="mb-2 nx-key-value--card"
        @mouseenter="onMouseEnter(index)"
        @mouseleave="onMouseLeave(index)"
      >
        <v-flex class="px-4 py-2 nx-primary--lighten-5">
          <v-text-field
            name="key"
            :label="keyLabel"
            :disabled="disableAdd"
            v-model="item.key"
            @blur="onBlur"
          ></v-text-field>

          <v-text-field
            v-if="isTextValueField"
            name="value"
            :label="valueLabel"
            v-model="item.value"
            @blur="onBlur"
          ></v-text-field>

          <v-textarea
            v-else-if="isTextareaValueField"
            name="value"
            :label="valueLabel"
            v-model="item.value"
            @blur="onBlur"
          ></v-textarea>

          <component
            :is="'HTMLField'"
            v-else-if="isHTMLValueField"
            name="value"
            :label="valueLabel"
            v-model="item.value"
            @blur="onBlur"
          ></component>
        </v-flex>

        <v-flex class="d-flex nx-primary--lighten-4 nx-key-value--actions" v-if="!disableMove || !disableDelete">
          <v-layout
            column
            text-xs-center
            align-center
            justify-space-between
            align-content-center>
            <v-btn icon flat class="nx-drag-handle nx-ui--level-2" v-if="!disableMove">
              <v-icon>drag_handle</v-icon>
            </v-btn>
            <v-btn icon flat @click="remove(index)" class="nx-ui--level-2" v-if="!disableDelete">
              <v-icon>delete</v-icon>
            </v-btn>

          </v-layout>
        </v-flex>
        <transition name="fade" v-if="!disableAdd">
          <v-btn absolute
                 icon
                 color="white"
                 class="nx-key-value--floating-button nx-ui--level-2"
                 v-show="showFloatingButtonAt(index)"
                 @click="addCardAt(index + 1)">
            <v-icon>add</v-icon>

          </v-btn>
        </transition>
      </v-layout>

    </transition-group>

    <nx-relation-empty-card v-if="isEmpty && disableAdd" :text="$t(disableAddEmptyText)" />
  </div>
</template>

<script>
import Sortable from 'sortablejs';
import uuid from 'uuid/v4';
import NxRelationEditorToolbar from '@/modules/relation/editor/components/Toolbar';
import NxRelationEmptyCard from '@/modules/relation/card/EmptyCard';

export default {
  name: 'nx-key-value-editor',
  components: { NxRelationEmptyCard, NxRelationEditorToolbar },
  props: {
    value: {
      type: Array,
      default: () => ([]),
    },
    keyLabel: {
      type: String,
      default: 'Clave',
    },
    valueLabel: {
      type: String,
      default: 'Valor',
    },
    title: {
      type: String,
      default: 'Relación clave valor',
    },
    valueField: {
      type: String,
      default: () => 'text',
      validator: v => ['text', 'textarea', 'html'],
    },
    disableAdd: {
      type: Boolean,
    },
    disableAddEmptyText: {
      type: String,
      default: 'message.common.emptyEditableParams',
    },
    disableMove: {
      type: Boolean,
    },
    disableDelete: {
      type: Boolean,
    },
    hint: {
      type: String,
    },
  },
  data() {
    return {
      sortablejs: null,
      activeIndex: null,
      moving: false,
    };
  },
  computed: {
    isEmpty() {
      return this.value.length === 0;
    },
    isHTMLValueField() {
      return this.valueField === 'html';
    },
    isTextValueField() {
      return this.valueField === 'text';
    },
    isTextareaValueField() {
      return this.valueField === 'textarea';
    },
  },
  methods: {
    addCardAt(index) {
      const value = this.value.slice();
      value.splice(index, 0, this.createNewCard());
      this.$emit('input', value);
    },
    /**
     * Returns an empty key-value card.
     * @return {{ key: string, value: string, uuid: string }}
     */
    createNewCard() {
      return { key: '', value: '', uuid: uuid() };
    },
    /**
     * Emit value on input blur, just to ensure that the form-element variant of this component can
     * update the parent form properly.
     */
    onBlur() {
      this.$emit('input', this.value);
    },
    onMouseEnter(index) {
      if (this.activeIndex !== index) {
        this.activeIndex = index;
      }
    },
    onMouseLeave(index) {
      if (this.activeIndex === index) {
        this.activeIndex = null;
      }
    },
    /**
     * Update value when sortable is updated.
     * @param oldIndex The position of the element moved.
     * @param newIndex The position where the element was moved into.
     */
    onSortUpdate({ oldIndex, newIndex }) {
      const value = this.value.slice();
      value.splice(newIndex, 0, value.splice(oldIndex, 1)[0]);
      this.$emit('input', value);
    },
    /**
     * When the sorting starts, update the `moving` property so the UI gets updated too.
     */
    onSortStart() {
      this.moving = true;
    },
    /**
     * When the sorting stops, update the `moving` property so the UI gets updated too.
     */
    onSortEnd() {
      this.moving = false;
    },
    /**
     * Deletes the item at the given index.
     * @param index
     */
    remove(index) {
      const value = this.value.slice();
      value.splice(index, 1);
      this.$emit('input', value);
    },
    /**
     * Returns whether the floating button at the given index should be displayed or not.
     * @param index
     * @return {boolean}
     */
    showFloatingButtonAt(index) {
      return !this.moving
        && (this.activeIndex === index || this.activeIndex === (index + 1));
    },
  },
  mounted() {
    if (!this.sortablejs) {
      this.sortablejs = Sortable.create(this.$refs.list.$el, {
        handle: '.nx-drag-handle',
        onUpdate: this.onSortUpdate,
        onStart: this.onSortStart,
        onEnd: this.onSortEnd,
      });
    }
  },
};
</script>

<style lang="stylus">
.nx-key-value--list {
  .sortable-ghost {
    border 1px dashed #41414E
  }

  .nx-key-value--floating-button {
    bottom: -22px
    left: 50%
    margin-left: -18px
    z-index: 1
  }

  .nx-key-value--card {
    position: relative
    transition: transform .3s
  }

  &.moving {
    .nx-key-value--card:not(.sortable-chosen) {
      transform: scale(.95);
      opacity: .7
    }
  }
}


.nx-key-value--actions
  max-width: 50px

.relation-list-move {
  transition: transform 1s;
}

.relation-list-enter, .relation-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
